import React from 'react';
import { act } from 'react-dom/test-utils';
import { render, screen, waitFor } from '@testing-library/react';

import useCreateUserForm, { emptyDraft, toPayload } from './useCreateUserForm';

const STATES = { US: [{ label: 'Virginia', value: 'VA' }], JP: [] };

const filled = (overrides = {}) => ({
  ...emptyDraft(),
  accountIds: ['7'],
  firstName: 'Jane',
  lastName: 'Doe',
  email: 'jane.doe@example.com',
  permissionIds: ['r1'],
  ...overrides,
});

// A probe rather than renderHook: @testing-library/react-hooks is not installed,
// and rendering the state is enough to assert on it. Same approach as
// useUsers.test.js.
let hook;

function Probe() {
  hook = useCreateUserForm();

  return (
    <div>
      <span data-testid="validity">
        {hook
          .stepValidity(STATES)
          .map(String)
          .join(',')}
      </span>
      <span data-testid="state-missing">
        {String(hook.isStateMissing(STATES))}
      </span>
      <span data-testid="state">{hook.draft.state}</span>
      <span data-testid="email-taken">{String(hook.checks.emailTaken)}</span>
      <span data-testid="mobile-valid">{String(hook.checks.mobileValid)}</span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;
const validAt = (step) => at('validity').split(',')[step];

const set = (field, value) => act(() => hook.setField(field, value));

const respondWith = (body) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok: true, json: async () => body });
};

beforeEach(() =>
  respondWith({ emailTaken: false, mobileTaken: false, mobileValid: true }),
);

afterEach(() => {
  delete global.fetch;
  hook = undefined;
});

describe('management/Users useCreateUserForm', () => {
  it('starts blank', () => {
    render(<Probe />);

    expect(hook.draft).toEqual(emptyDraft());
  });

  it('defaults the mobile country code to +1, as the design shows', () => {
    render(<Probe />);

    expect(hook.draft.mobileCountryCode).toEqual('1');
  });

  it('updates a field', () => {
    render(<Probe />);

    set('firstName', 'Jane');

    expect(hook.draft.firstName).toEqual('Jane');
  });

  it('resets to blank', () => {
    render(<Probe />);

    set('firstName', 'Jane');
    act(() => hook.reset());

    expect(hook.draft).toEqual(emptyDraft());
  });

  describe('step validity', () => {
    it('needs an account for step 1', () => {
      render(<Probe />);

      expect(validAt(0)).toEqual('false');

      set('accountIds', ['7']);

      expect(validAt(0)).toEqual('true');
    });

    it('needs a name and a well-formed email for step 2', () => {
      render(<Probe />);

      set('firstName', 'Jane');
      set('lastName', 'Doe');
      set('email', 'nope');

      expect(validAt(1)).toEqual('false');

      set('email', 'jane@example.com');

      expect(validAt(1)).toEqual('true');
    });

    it('rejects whitespace-only names', () => {
      render(<Probe />);

      set('firstName', '   ');
      set('lastName', 'Doe');
      set('email', 'jane@example.com');

      expect(validAt(1)).toEqual('false');
    });

    it('needs a permission for step 4', () => {
      render(<Probe />);

      expect(validAt(3)).toEqual('false');

      set('permissionIds', ['r1']);

      expect(validAt(3)).toEqual('true');
    });

    // Ticking only Management Role leaves permissionIds empty, but it is still a
    // permission.
    it('counts a management role on its own as a permission', () => {
      render(<Probe />);

      set('managementRoleId', 'r3');

      expect(validAt(3)).toEqual('true');
    });
  });

  describe('state and country', () => {
    it('does not ask for a state before a country is chosen', () => {
      render(<Probe />);

      expect(at('state-missing')).toEqual('false');
      expect(validAt(2)).toEqual('true');
    });

    it('asks for a state once a country with states is chosen', () => {
      render(<Probe />);

      act(() => hook.setCountryCode('US'));

      expect(at('state-missing')).toEqual('true');
      expect(validAt(2)).toEqual('false');
    });

    it('is satisfied once a state is chosen', () => {
      render(<Probe />);

      act(() => hook.setCountryCode('US'));
      set('state', 'VA');

      expect(at('state-missing')).toEqual('false');
    });

    // A country the shared geo data has no states for must not block the step.
    it('does not ask for a state a country has none of', () => {
      render(<Probe />);

      act(() => hook.setCountryCode('JP'));

      expect(at('state-missing')).toEqual('false');
    });

    // The states offered come from the country, so one chosen under the previous
    // country is not in the new list — which is what surfaces the warning.
    it('clears the state when the country changes', () => {
      render(<Probe />);

      act(() => hook.setCountryCode('US'));
      set('state', 'VA');
      act(() => hook.setCountryCode('JP'));

      expect(at('state')).toEqual('');
    });

    it('keeps the state when the country is set to what it already was', () => {
      render(<Probe />);

      act(() => hook.setCountryCode('US'));
      set('state', 'VA');
      act(() => hook.setCountryCode('US'));

      expect(at('state')).toEqual('VA');
    });
  });

  describe('duplicate checks', () => {
    it('reports a taken email', async () => {
      respondWith({ emailTaken: true, mobileTaken: false, mobileValid: true });
      render(<Probe />);

      set('email', 'taken@example.com');
      hook.checkEmail();

      await waitFor(() => expect(at('email-taken')).toEqual('true'));
    });

    it('blocks step 2 on a taken email', async () => {
      respondWith({ emailTaken: true, mobileTaken: false, mobileValid: true });
      render(<Probe />);

      set('firstName', 'Jane');
      set('lastName', 'Doe');
      set('email', 'taken@example.com');
      hook.checkEmail();

      await waitFor(() => expect(validAt(1)).toEqual('false'));
    });

    it('does not ask about an address that is not yet an email', () => {
      render(<Probe />);

      set('email', 'jane@');
      hook.checkEmail();

      expect(global.fetch).not.toHaveBeenCalled();
    });

    it('does not ask about a blank mobile', () => {
      render(<Probe />);

      hook.checkMobile();

      expect(global.fetch).not.toHaveBeenCalled();
    });

    it('sends both halves of the mobile', () => {
      render(<Probe />);

      set('mobile', '2024561111');
      set('mobileCountryCode', '52');
      hook.checkMobile();

      expect(global.fetch.mock.calls[0][0]).toContain('mobile=2024561111');
      expect(global.fetch.mock.calls[0][0]).toContain('mobile_country_code=52');
    });

    // The create itself still rejects a duplicate, so a failed check must not
    // leave the admin unable to finish.
    it('does not block on a failed check', async () => {
      global.fetch = jest.fn().mockRejectedValue(new Error('offline'));
      render(<Probe />);

      set('email', 'jane@example.com');
      hook.checkEmail();

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());
      expect(at('email-taken')).toEqual('false');
    });
  });
});

describe('management/Users toPayload', () => {
  it("maps the draft onto the controller's field names", () => {
    const payload = toPayload(
      filled({ dmsId: 'DMS-1', jobRole: 'fleet_manager', postalCode: '23219' }),
    );

    expect(payload.user).toMatchObject({
      first_name: 'Jane',
      last_name: 'Doe',
      email: 'jane.doe@example.com',
      dms_id: 'DMS-1',
      job_role: 'fleet_manager',
      postal_code: '23219',
    });
  });

  it('trims what the admin typed', () => {
    expect(
      toPayload(filled({ firstName: '  Jane  ' })).user.first_name,
    ).toEqual('Jane');
  });

  // The management role is one of the permission rows, so it travels with them.
  it('sends the management role alongside the other permissions', () => {
    const payload = toPayload(
      filled({ permissionIds: ['r1', 'r2'], managementRoleId: 'r3' }),
    );

    expect(payload.role_ids).toEqual(['r1', 'r2', 'r3']);
  });

  it('omits an unchosen management role rather than sending a blank', () => {
    expect(toPayload(filled({ managementRoleId: '' })).role_ids).toEqual([
      'r1',
    ]);
  });

  // The model clears the country code without a number, so sending one alone
  // would be noise.
  it('drops the mobile country code when there is no number', () => {
    const payload = toPayload(filled({ mobile: '', mobileCountryCode: '52' }));

    expect(payload.user.mobile_country_code).toEqual('');
  });

  it('keeps the country code alongside a number', () => {
    const payload = toPayload(
      filled({ mobile: '2024561111', mobileCountryCode: '52' }),
    );

    expect(payload.user.mobile_country_code).toEqual('52');
  });

  it('sends both discoverability flags', () => {
    const payload = toPayload(
      filled({ hiddenExternal: true, hiddenInternal: false }),
    );

    expect(payload.discoverability).toEqual({
      hidden_external: true,
      hidden_internal: false,
    });
  });
});
